// @flow
import * as React from 'react';
import styled from 'styled-components';
import { sprintf } from 'sprintf-js';
import interpolateComponents from 'interpolate-components';
import type { Notification } from 'api/currentUser';
import { trackClicked, trackEvent } from 'utils/analytics';
import Box from 'components/Box';
import UserNotification, { StatusTag } from 'components/UserNotification';
import { Link } from 'components/RouterLink';
import Text from 'components/Text';
import { colors, fontWeights } from 'theme';

const { useEffect } = React;

const StyledLink = styled(Link)`
  font-weight: ${fontWeights.semiBold};
`;

const NotificationContent = ({
  notification,
  dismiss
}: {
  notification: Notification,
  dismiss: Function
}) => {
  switch (notification.type) {
    case 'NEW_APPLICATIONS_BY_LISTING':
      return (
        <>
          <StyledLink
            onClick={() => {
              trackClicked('User Menu Notification', {
                notification_type: notification.type,
                listing_id: notification.data.id,
                listing_type: notification.data.type,
                listing_name: notification.data.name,
                org_id: notification.data.orgId,
                application_count: notification.data.applications.length
              });
              dismiss();
            }}
            to={`/admin/${
              notification.data.orgId
            }/${notification.data.type.toLowerCase()}s/${notification.data.id}`}
          >
            {notification.data.name}
          </StyledLink>
          <Box mt="4px">
            {interpolateComponents({
              mixedString: sprintf(
                notification.data.count > 1
                  ? getText(
                      '{{strong}}%(count)s{{/strong}} unread applications'
                    )
                  : getText(
                      '{{strong}}%(count)s{{/strong}} unread application'
                    ),
                {
                  count: notification.data.count
                }
              ),
              components: {
                strong: <strong />
              }
            })}
          </Box>
          {notification.data.newCount > notification.data.count && (
            <Box mt="4px" color={colors.battleshipGrey}>
              {interpolateComponents({
                mixedString: sprintf(
                  getText('%(count)s still marked {{status}}new{{/status}}'),
                  {
                    count: notification.data.newCount
                  }
                ),
                components: {
                  status: (
                    <StatusTag
                      ml="3px"
                      color={colors.waterBlue}
                      boxShadow={`0 0 0 1px ${colors.silver}`}
                    />
                  )
                }
              })}
            </Box>
          )}
        </>
      );
    default:
      return null;
  }
};

const Notifications = ({
  notifications,
  dismiss,
  open
}: {
  notifications: Array<Notification>,
  dismiss: Function,
  open: boolean
}) => {
  const sortedNotifications = notifications
    .slice()
    .sort((a, b) => new Date(b.created) - new Date(a.created));
  const primaryNotifications = sortedNotifications.slice(0, 3);
  const otherNotifications =
    sortedNotifications.length > 3 ? sortedNotifications.slice(3) : null;
  const otherListingApplicationNotifications = otherNotifications
    ? otherNotifications.filter(
        notification => notification.type === 'NEW_APPLICATIONS_BY_LISTING'
      )
    : null;
  const otherListingApplicationData =
    otherListingApplicationNotifications &&
    otherListingApplicationNotifications[0].type ===
      'NEW_APPLICATIONS_BY_LISTING'
      ? otherListingApplicationNotifications[0].data
      : null;

  useEffect(
    () => {
      if (open && notifications.length) {
        trackEvent('Displayed User Menu Notifications', {
          notification_types: notifications.reduce(
            (acc, notification) =>
              acc.includes(notification.type)
                ? acc
                : [...acc, notification.type],
            []
          )
        });
      }
    },
    [notifications, open]
  );

  return (
    <>
      {primaryNotifications.map(notification => {
        const dismissNotification = () =>
          dismiss(
            notification.type === 'NEW_APPLICATIONS_BY_LISTING'
              ? notification.data.applications.map(
                  application => application.notificationId
                )
              : [notification.id]
          );
        return (
          <Box
            p={12}
            minWidth={260}
            borderTop={`1px solid ${colors.aluminum}`}
            key={
              notification.type === 'NEW_APPLICATIONS_BY_LISTING'
                ? notification.data.applications[0].notificationId
                : notification.id
            }
          >
            <UserNotification dismiss={dismissNotification}>
              <NotificationContent
                notification={notification}
                dismiss={dismissNotification}
              />
            </UserNotification>
          </Box>
        );
      })}
      {otherListingApplicationNotifications && otherListingApplicationData && (
        <Box p={12} minWidth={260} borderTop={`1px solid ${colors.aluminum}`}>
          <Link
            to={`/admin/${otherListingApplicationData.orgId}${
              otherListingApplicationNotifications.length === 1
                ? `/${otherListingApplicationData.type.toLowerCase()}s/${
                    otherListingApplicationData.id
                  }`
                : ''
            }`}
          >
            <Text fontWeight={fontWeights.semiBold}>
              {sprintf(
                otherListingApplicationNotifications.length > 1
                  ? getText('%(count)s more listings with unread applications')
                  : getText('%(count)s more listing with unread applications'),
                {
                  count: otherListingApplicationNotifications.length
                }
              )}
            </Text>
          </Link>
        </Box>
      )}
    </>
  );
};

export default Notifications;
